import { Stack } from '@mui/material'
import Typography from '@mui/material/Typography'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppBarContent } from '@/components/layouts/Page'
import { MarketProgramFilterTabs } from '@/features/site/components/availability/MarketProgramFilterTabs'
import { RevenueContent } from '@/features/site/components/revenue/RevenueContent'
import { useRevenueTimeSeriesQuery } from '@/features/site/hooks/useRevenueTimeSeriesQuery'
import SiteRevenueAppBar from '@/features/site/pages/components/SiteRevenueAppBar'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type { RevenueDataRow, RevenuePeriod } from '@/features/site/types/revenue'
import type { Site } from '@/features/site/types/site'
import {
  getAvailableYearsForMarketProgram,
  getRevenueTimeSeriesEndTime,
  getRevenueTimeSeriesStartTime,
} from '@/features/site/utils/revenueUtils/dateUtils'
import { formatRevenueTimeSeries } from '@/features/site/utils/revenueUtils/formatRevenueTimeSeries'
import { getMarketProgramsOptions } from '@/features/site/utils/revenueUtils/marketProgramOptions'
import { compareMarketPrograms } from '@/features/site/utils/sortMarketPrograms'
import type { MarketProgramType } from '@/types/marketProgramType'

type SiteRevenueLegacyViewProps = {
  site: Site
  revenuePeriods: RevenuePeriod[] | null
  marketPrograms: MarketProgram[] | null
}

export function SiteRevenueLegacyView({ site, revenuePeriods, marketPrograms }: Readonly<SiteRevenueLegacyViewProps>) {
  const { t } = useTranslation()
  const { setAppBarContent } = useAppBarContent()

  const timezone = site.timezone ?? DateTime.utc().zoneName

  const relevantMarketPrograms = useMemo(() => {
    return getMarketProgramsOptions(marketPrograms, revenuePeriods).toSorted((a, b) => compareMarketPrograms(t, a, b))
  }, [marketPrograms, revenuePeriods, t])

  const [marketProgramSelection, setMarketProgramSelection] = useState<MarketProgram>()
  const [yearOptions, setYearOptions] = useState<string[]>([])
  const [yearSelection, setYearSelection] = useState<string>('')

  const handleMarketProgramChange = (newValue: MarketProgramType) => {
    const newMarketProgram = relevantMarketPrograms.find((program) => program.type === newValue)!
    setMarketProgramSelection(newMarketProgram)
    const availableYears = getAvailableYearsForMarketProgram(revenuePeriods ?? [], timezone, newMarketProgram)
    setYearOptions(availableYears)

    // In case the new available years do not contain the already selected year, select the most recent year in the options.
    if (!availableYears.includes(yearSelection)) {
      setYearSelection(availableYears[0])
    }
  }

  const revenueTimeSeriesStartTime = getRevenueTimeSeriesStartTime(
    revenuePeriods,
    timezone,
    marketProgramSelection,
    yearSelection,
  )

  const revenueTimeSeriesEndTime = getRevenueTimeSeriesEndTime(
    revenuePeriods,
    timezone,
    marketProgramSelection,
    yearSelection,
  )

  useEffect(() => {
    if (!marketProgramSelection && relevantMarketPrograms.length > 0) {
      setMarketProgramSelection(relevantMarketPrograms[0])
    }
  }, [relevantMarketPrograms, marketProgramSelection])

  useEffect(() => {
    const availableYears = getAvailableYearsForMarketProgram(revenuePeriods ?? [], timezone, marketProgramSelection)
    setYearOptions(availableYears)
    if (availableYears.length > 0) {
      setYearSelection(
        yearSelection === '' || !availableYears.includes(yearSelection) ? availableYears[0] : yearSelection,
      )
    }
  }, [revenuePeriods, marketProgramSelection, timezone, yearSelection])

  const { revenueTimeSeries, isFetching: isLoadingRevenueTimeSeries } = useRevenueTimeSeriesQuery(
    {
      customerUuid: site.uuid!,
      serviceId: marketProgramSelection?.id ?? 0,
      startTime: revenueTimeSeriesStartTime ?? DateTime.local({ zone: timezone }),
      endTime: revenueTimeSeriesEndTime ?? DateTime.local({ zone: timezone }),
    },
    {
      enabled: Boolean(revenueTimeSeriesStartTime) && Boolean(revenueTimeSeriesEndTime),
    },
  )

  const revenueData: RevenueDataRow[] = useMemo(() => {
    return formatRevenueTimeSeries(revenueTimeSeries, timezone)
  }, [revenueTimeSeries, timezone])

  useEffect(() => {
    setAppBarContent(
      <SiteRevenueAppBar
        availableYears={yearOptions}
        revenueData={revenueData}
        selectedMarketProgramType={marketProgramSelection?.type ?? ''}
        setYearSelection={setYearSelection}
        siteName={site.name ?? ''}
        yearSelection={yearSelection}
      />,
    )
  }, [yearOptions, revenueData, site.name, marketProgramSelection?.type, yearSelection, setAppBarContent])

  if (!revenuePeriods || revenuePeriods.length === 0) {
    return (
      <Typography align={'center'} sx={{ m: 4 }}>
        {t('customer_revenue.no_revenue_data')}
      </Typography>
    )
  }

  return (
    <Stack direction="column" mb={2} mt={2} spacing={3} sx={{ width: '100%' }}>
      <MarketProgramFilterTabs
        marketPrograms={relevantMarketPrograms.map((program) => program?.type as MarketProgramType)}
        value={marketProgramSelection?.type}
        onChange={handleMarketProgramChange}
      />
      <RevenueContent
        isLoadingRevenueTimeSeries={isLoadingRevenueTimeSeries}
        revenueData={revenueData}
        selectedYear={yearSelection}
      />
    </Stack>
  )
}
